import {createUseStyles} from 'react-jss';
import {FC, ReactElement, ReactNode, useRef, useState, useEffect} from 'react';
import {CssBreakpoints} from 'styles/responsive';
import {DescribedText} from 'features/terms/components/described-text';
import {useWindowEventListener} from 'common/hooks/use-event-listener';
import {getEnv} from 'environment/public.environment';

const useDetailRowStyles = createUseStyles({
  flexContainer: {
    alignItems: 'baseline',
    display: 'flex',
    flexFlow: 'row wrap',
    position: 'relative',
    margin: '1rem 0 0.5rem',
    flex: 1,

    [`@media (max-width: ${CssBreakpoints.MD}px)`]: {
      flexDirection: 'column',

      '@media print': {
        flexDirection: 'row',
      },
    },
  },
  defaultContentRow: {
    '@media screen': {
      gap: '.5rem',
    },

    [`@media (max-width: ${CssBreakpoints.SM})`]: {
      flexFlow: 'wrap',
      flexDirection: 'unset',
    },

    [`@media (max-width: ${CssBreakpoints.XS}px)`]: {
      flexFlow: 'nowrap',
    },
  },
  defaultContentLabel: {
    fontWeight: '600',
    whiteSpace: 'nowrap',
    '@media print': {
      flex: '2 1 40%',
      margin: '0',
    },
  },
  mainContentLabel: {
    fontWeight: '600',
    whiteSpace: 'nowrap',
    flex: '2 1 40%',
    margin: '0',
    position: 'relative',
    width: '100%',
  },
  defaultContentText: {
    listStyleType: 'none',
    margin: '0',
    marginLeft: '0',
    '@media print': {
      flex: '2 1 60%',
    },
  },
  mainContentText: {
    listStyleType: 'none',
    margin: '0',
    marginLeft: '0',
    flex: '2 1 60%',
  },
  listElement: {
    [`@media (max-width: ${CssBreakpoints.XS}px)`]: {
      margin: '0.3em 0 0 0.5em',
    },
  },
  description: {
    margin: 0,
  },
  descriptionHead: {
    display: 'block',
  },
  label: {
    margin: 0,
    textAlign: 'left',
    [`@media (max-width: ${CssBreakpoints.SM})`]: {
      marginRight: '0.5em',
    },
  },
});

export interface DetailRowProps {
  children: ReactNode;
  label: string;
  isDefaultContentRow?: boolean;
  description?: string | ReactElement;
}

export const DetailRow: FC<DetailRowProps> = ({label, children, isDefaultContentRow, description}) => {
  const styles = useDetailRowStyles();
  const popoverAttachRef = useRef<HTMLParagraphElement>(null);
  const popoverWrapperRef = useRef<HTMLDivElement>(null);

  const [descWidth, setDescWidth] = useState<number | undefined>();

  const calcWrapperWidth = () => {
    if (isDefaultContentRow) {
      setDescWidth((popoverAttachRef.current?.clientWidth ?? 0) - 16);
    } else if (window.innerWidth > CssBreakpoints.MD) {
      setDescWidth((popoverWrapperRef.current?.clientWidth ?? 0) - 16);
    } else {
      setDescWidth(undefined);
    }
  };

  useWindowEventListener('resize', calcWrapperWidth);
  useEffect(calcWrapperWidth, []);

  return (
    <div
      data-testid='detail-row'
      className={`${styles.flexContainer} ${isDefaultContentRow ? styles.defaultContentRow : ''}`}
    >
      <div
        className={isDefaultContentRow ? styles.defaultContentLabel : styles.mainContentLabel}
        ref={popoverWrapperRef}
      >
        {getEnv().features.fellesDatakatalog && description ? (
          <DescribedText
            description={
              <>
                <p className={styles.description}>
                  <b className={styles.descriptionHead}>{label}</b>
                  {description}
                </p>
              </>
            }
            descWidth={descWidth}
            screenreaderText={label}
          >
            <p data-testid='row-label' className={styles.label} ref={popoverAttachRef}>
              {label}:
            </p>
          </DescribedText>
        ) : (
          <p data-testid='row-label' className={styles.label} ref={popoverAttachRef}>
            {label}:
          </p>
        )}
      </div>
      <div
        data-testid='row-content'
        className={isDefaultContentRow ? styles.defaultContentText : styles.mainContentText}
      >
        {children}
      </div>
    </div>
  );
};
